import { useEffect, useMemo, useRef, useState } from 'react';
import { navigate } from '@reach/router';
import mixpanel from 'mixpanel-browser';
import cx from 'classnames';

import CauzeSpinner from 'components/CauzeSpinner/CauzeSpinner';
import pinDrop from 'assets/images/icons/pin/pin-drop.png';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronLeft,
  faChevronRight,
  faSearch,
} from '@fortawesome/free-solid-svg-icons';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import useMobile from 'hooks/useMobile';
import useSearchStore from 'stores/SearchStoreV2';
import { ReactComponent as PencilIcon } from 'assets/images/icons/svgs/pencil.svg';

import SearchItem from 'components/SearchItem/SearchItem';
import { CharityListItem } from 'components/CharityList/CharityList';
import Currency from 'components/Currency/Currency';
import CategoryList from 'components/CategoryList/CategoryList';
import { getZipFromIP } from 'util/getZipFromIP';
import LocationSearch from 'components/LocationSearch/LocationSearch';

export const EmployeeMatchSection = ({ match }) => {
  const totalAmount = match?.matchLimit ?? 0;
  const usedAmount = match?.matchLimit - match?.currentEntityRemaining ?? 0;
  const progressValue = (usedAmount / totalAmount) * 100;

  const getMultiplierText = () => {
    if (match?.multiplier === 1) {
      return 'doubled';
    }

    if (match?.multiplier === 2) {
      return 'tripled';
    }

    if (match?.multiplier === 3) {
      return 'quadrupled';
    }

    return `${match?.multiplier}x`;
  };

  if (!match) {
    return (
      <div className="h-[189px] w-full bg-lightgray-e8e animate-pulse rounded-[10px]"></div>
    );
  }

  return (
    <div className="p-5 flex flex-col border border-solid border-lightgray-f1f rounded-[10px] employee-match-section">
      <div className="font-agenda-bold text-center text-[26px]">
        You have <Currency amount={match?.currentEntityRemaining} /> available
        for matching this year
      </div>
      <div className="text-gray-949 text-center">
        Every dollar you donate will be {getMultiplierText()} by Altman Solon
      </div>
      <div className="w-full pt-6 font-agenda">
        <div className="flex justify-between text-xs text-gray-949">
          <div>
            Used: <Currency className="font-agenda-bold" amount={usedAmount} />
          </div>
          <div>
            Available:{' '}
            <Currency
              className="font-agenda-bold"
              amount={totalAmount - usedAmount}
            />
          </div>
        </div>
        <progress
          value={progressValue}
          max="100"
          className="progress-bar"
          aria-label={`Matching funds progress: ${progressValue.toFixed(
            1,
          )}% used`}
        />
        <div className="text-xs text-gray-949 text-center w-full pt-1">
          You have used{' '}
          <Currency className="font-agenda-bold" amount={usedAmount} /> of your{' '}
          <Currency className="font-agenda-bold" amount={totalAmount} /> annual
          match
        </div>
      </div>
      {/* <hr className="border-lightgray-f1f my-4" />
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5 font-agenda-medium leading-tight">
        <div className="p-5 border border-solid border-lightgray-f1f rounded-[10px] flex gap-x-[14px]">
          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="lucide lucide-trending-up w-5 h-5 text-blue-600"
              aria-hidden="true"
            >
              <path d="M16 7h6v6"></path>
              <path d="m22 7-8.5 8.5-5-5L2 17"></path>
            </svg>
          </div>
          <div>
            Your Altman Solon colleagues have created{' '}
            <span className="text-blue-600">
              <Currency amount={match?.employeeDonatedAmount} />
            </span>{' '}
            in total impact this year
          </div>
        </div>
        <div className="p-5 border border-solid border-lightgray-f1f rounded-[10px] flex gap-x-[14px]">
          <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="lucide lucide-users w-5 h-5 text-green-600"
              aria-hidden="true"
            >
              <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
              <path d="M16 3.128a4 4 0 0 1 0 7.744"></path>
              <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
              <circle cx="9" cy="7" r="4"></circle>
            </svg>
          </div>
          <div>
            <span className="text-green-600">
              {match?.employeeDonatedCount}
            </span>{' '}
            colleague{match?.employeeDonatedCount === 1 ? '' : 's'} used their
            employer match
          </div>
        </div>
      </div> */}
    </div>
  );
};

export const PopularNonprofitsSection = ({
  parsedLocationString,
  user,
  userLoading,
  localEntities,
  localLoading,
  onApply,
}) => {
  const [zip, setZip] = useState(localStorage.getItem('cauze-local-zip') || '');
  const [isEditing, setIsEditing] = useState(false);
  const { isMobile } = useMobile();
  const prevRef = useRef(null);
  const nextRef = useRef(null);
  const [swiperRef, setSwiperRef] = useState(null);
  const [isAtBeginning, setIsAtBeginning] = useState(true);
  const [isAtEnd, setIsAtEnd] = useState(false);

  const handleSlideChange = (swiper) => {
    setIsAtBeginning(swiper.isBeginning);

    setIsAtEnd(swiper.isEnd);
  };
  const selected = [];
  const renderContent = useMemo(() => {
    if (userLoading) {
      return <CauzeSpinner />;
    }

    if (localLoading) {
      return (
        <div className="flex gap-x-2">
          <div className="h-[170px] w-[160px] rounded-[10px] bg-lightgray-e8e animate-pulse" />
          <div className="h-[170px] w-[160px] rounded-[10px] bg-lightgray-e8e animate-pulse" />
          <div className="h-[170px] w-[160px] rounded-[10px] bg-lightgray-e8e animate-pulse" />
          <div className="h-[170px] w-[160px] rounded-[10px] bg-lightgray-e8e animate-pulse" />
        </div>
      );
    }

    if ((!user?.zip && !zip) || isEditing || localEntities.length === 0) {
      return (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            onApply?.(zip, true);
            localStorage.setItem('cauze-local-zip', zip);
            setIsEditing(false);
            return false;
          }}
          className="flex flex-col gap-y-3 bg-lightgray-f7f rounded-[0.625rem] px-8 pt-4 pb-8 text-center font-agenda"
        >
          <img
            src={pinDrop}
            className="w-12 mx-auto rounded-full bg-white p-2"
          />
          <div className="font-agenda-bold text-[26px]">
            Where in the world are you?
          </div>
          <div className="text-lg">
            Find local cauzes, and help your friends find you.
          </div>
          <div className="w-full">
            <LocationSearch
              onChange={(location) => {
                if (location?.zipcode) {
                  setZip(location.zipcode);
                }
              }}
            />
          </div>
          <button className="cz-btn-brand mt-8" type="submit">
            Apply
          </button>
        </form>
      );
    }

    return (
      <div className="discover-charity-list">
        <Swiper
          className={'charity-list'}
          modules={[Navigation]}
          spaceBetween={8}
          slidesPerView={isMobile ? 2.1 : 3.5}
          onSlideChange={handleSlideChange}
          navigation={{
            prevEl: 'swiper-arrows-above-button-prev',
            nextEl: 'swiper-arrows-above-button-next',
          }}
          onInit={(swiper) => {
            setSwiperRef(swiper);
            swiper.prevEl = prevRef.current;
            swiper.nextEl = nextRef.current;
            swiper.navigation.update();
          }}
          onSwiper={setSwiperRef}
        >
          {localEntities.map((charity) => (
            <SwiperSlide key={charity.id}>
              <CharityListItem
                charity={charity}
                onSelect={(charityId) => {
                  navigate(`/charity/${charityId}`);
                }}
                isSelected={selected.includes(charity.id)}
              />
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    );
  }, [localEntities, userLoading, user, localLoading, zip, isEditing]);

  useEffect(() => {
    if (!user && !zip && !isEditing) {
      getZipFromIP().then((_zip) => {
        if (_zip) {
          setZip(_zip);
          onApply?.(_zip, false);
        }
      });
    }
  }, [zip, user, isEditing]);

  useEffect(() => {
    const zipCode = localStorage.getItem('cauze-local-zip');
    if (zipCode && !user) {
      onApply?.(zipCode, false);
    }
  }, [onApply, user]);

  return (
    <>
      <div className="text-[22px] leading-none mt-8 mb-5 flex justify-between">
        <div className="flex flex-col lg:flex-row items-center gap-x-1">
          <span className="font-agenda-bold h-[20px]">
            Popular Local Nonprofits{' '}
          </span>
          <div>
            {Boolean(parsedLocationString || user?.zip) && (
              <span className="font-agenda text-left w-full lg:w-auto">
                ({parsedLocationString || user?.zip})
              </span>
            )}
            {localEntities.length > 0 && (
              <button
                className="mr-1 ml-3 m-0 p-0 pb-1 pt-1 bg-transparent border-none cursor-pointer"
                onClick={() => {
                  setIsEditing(!isEditing);
                }}
              >
                <PencilIcon width="12" height="12" />
              </button>
            )}
          </div>
        </div>
        {swiperRef && swiperRef.allowSlideNext && !isMobile && (
          <div className="flex gap-2 items-center justify-center">
            {/* Custom Navigation Buttons */}
            <div
              ref={prevRef}
              onClick={() => swiperRef.slidePrev()}
              className={`py-1.5 px-2.5  flex items-center justify-center border border-solid border-[#adadb6] cursor-pointer rounded-full  swiper-arrows-above-button-prev ${
                isAtBeginning ? '!cursor-default opacity-40' : ''
              }`}
            >
              <FontAwesomeIcon
                icon={faChevronLeft}
                size={'xs'}
                style={{ color: '#2a2a33', transform: 'translateX(-1px)' }}
              />
            </div>
            <div
              ref={nextRef}
              onClick={() => {
                swiperRef.slideNext();
              }}
              className={`py-1.5 px-2.5  flex items-center justify-center border border-solid border-[#adadb6] cursor-pointer rounded-full  swiper-arrows-above-button-next ${
                isAtEnd ? '!cursor-default opacity-40' : ''
              }`}
            >
              <FontAwesomeIcon
                icon={faChevronRight}
                size={'xs'}
                style={{ color: '#2a2a33', transform: 'translateX(1px)' }}
              />
            </div>
          </div>
        )}
      </div>
      {renderContent}
    </>
  );
};

export const SearchNonprofitsSection = ({
  activeEntity,
  uiStore,
  authStore,
}) => {
  const PAGE_SIZE = 50;

  const searchStore = useSearchStore();
  const [search, setSearch] = useState('');
  const [previousSearch, setPreviousSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [categories, setCategories] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);

  const showResults = searchResults.length > 0 && !isLoading;
  const showEmpty = searchResults.length === 0 && !isLoading;
  const canLoadMore = page * PAGE_SIZE < total;

  const onDonate = (charityId) => {
    mixpanel.track('Search Result Click', {
      channel: 'web',
      type: 'charity',
      id: charityId,
      withGift: !!uiStore.savedUiState.userToken,
    });

    navigate(`/charity/${charityId}`);
  };

  useEffect(() => {
    if (searchStore.matchCompanyId && searchStore.topics.length) {
      setIsLoading(true);
      searchStore
        .fetchCharities({
          query: '',
          includeCompanyTopics: true,
        })
        .then(({ results, totalCount }) => {
          setSearchResults(results);
          setTotal(totalCount);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [searchStore.matchCompanyId, searchStore.topics.length]);

  return (
    <div className="mt-8">
      <div className="flex justify-between items-center mb-5">
        <div className="font-agenda-bold text-[22px] leading-none my-auto">
          Employer Match Eligible Charities
        </div>
        <div className="text-sm text-gray-949 font-agenda my-auto">
          {searchStore.getCompanyTopicCharityCount()} eligible charities
        </div>
      </div>
      <div className="relative mb-5">
        <FontAwesomeIcon
          icon={faSearch}
          style={{ color: '#aaaaaa' }}
          className="absolute left-4 top-1/2 -translate-y-1/2"
        />
        <input
          className="w-full pl-10 pr-4 py-3 rounded-[10px] border border-lightgray-e0e border-solid focus:outline-none"
          placeholder="Search eligible charities..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onBlur={async () => {
            if (search !== previousSearch) {
              setIsLoading(true);
              const { results, totalCount } = await searchStore.fetchCharities({
                query: search,
                categories: categories,
                includeCompanyTopics: true,
              });
              setIsLoading(false);
              setPreviousSearch(search);
              setSearchResults(results);
              setTotal(totalCount);
            }
          }}
          onKeyDown={async (e) => {
            if (e.key === 'Enter') {
              if (search !== previousSearch) {
                setIsLoading(true);
                const { results, totalCount } =
                  await searchStore.fetchCharities({
                    query: search,
                    categories: categories,
                    includeCompanyTopics: true,
                  });
                setIsLoading(false);
                setSearchResults(results);
                setPreviousSearch(search);
                setTotal(totalCount);
              }
            }
          }}
        />
      </div>
      <CategoryList
        title=""
        loading={false}
        navigateOnSearch={false}
        activeCategories={categories}
        onClick={async (categories) => {
          setCategories(categories);
          setIsLoading(true);
          const { results, totalCount } = await searchStore.fetchCharities({
            query: search,
            categories: categories,
            includeCompanyTopics: true,
          });
          setIsLoading(false);
          setSearchResults(results);
          setTotal(totalCount);
        }}
      />
      <div
        className={cx('text-sm text-gray-949 font-agenda my-auto mb-2', {
          hidden: searchResults.length === 0 || isLoading,
        })}
      >
        Showing {total} of {searchStore.getCompanyTopicCharityCount()} eligible
        charities
      </div>
      <div>
        <div>
          <div className="results-container gap-y-3.5 flex flex-col">
            {isLoading && <CauzeSpinner />}
            {showEmpty && (
              <div className="empty-item flex-column flex-align-center">
                <div className="flex-row">
                  <div className="flex-column">
                    <p className="text-bold">Sorry, We Couldn't Find a Match</p>
                    <p className="text-normal">
                      Try a different search item or check out
                      <br />
                      some of our suggestions below
                    </p>
                  </div>
                </div>
                <div className="flex-row flex-justify-end flex-align-center"></div>
              </div>
            )}
            {showResults &&
              searchResults.map((charity, index) => (
                <SearchItem
                  charity={charity}
                  userContext={activeEntity.userContext}
                  onDonate={() => onDonate(charity.id)}
                  onLearnMore={() => {
                    uiStore.openModal('PROFILE_MODAL', {
                      id: charity.id,
                      entityType: 'CHARITY',
                    });
                  }}
                  onFollowCharity={searchStore.followCharity}
                  onFollowGiver={searchStore.onFollowGiver}
                  key={`${index}-${charity.name}`}
                  index={index}
                  isAuthenticated={authStore.isAuthenticated}
                  authStore={authStore}
                />
              ))}
          </div>
        </div>
        <div>
          <div className="results-container">
            {isLoadingMore && <CauzeSpinner />}
            {canLoadMore && !isLoading && !isLoadingMore && (
              <div className="w-full flex">
                <div className="mx-auto">
                  <button
                    className="cz-btn-outline brand mt-5"
                    onClick={() => {
                      setPage(page + 1);
                      setIsLoadingMore(true);
                      searchStore
                        .fetchCharities({
                          query: search,
                          categories: categories,
                          includeCompanyTopics: true,
                          page: page + 1,
                          pageSize: PAGE_SIZE,
                        })
                        .then(({ results, totalCount }) => {
                          setSearchResults([...searchResults, ...results]);
                          setTotal(totalCount);
                        })
                        .finally(() => {
                          setIsLoadingMore(false);
                        });
                    }}
                  >
                    Load More Results
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
